import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, MessageCircle, RefreshCw, Sparkles, X as XIcon,
} from "lucide-react";
import {
  platformListConciergeShadow,
  platformGetConciergeShadow,
  platformReviewConciergeShadow,
} from "../../lib/api";

/**
 * Sprint 4 — Public Concierge Shadow Review.
 *
 * Read-only listing of every anonymous concierge message that came through
 * the shadow-mode endpoint, plus a review dialog for admins to approve /
 * reject / flag-for-edit each generated candidate. Every row is
 * tenant-scoped by construction — the API rejects cross-tenant reads.
 */
const PAGE_SIZE = 25;
const STATUSES = [
  "", "candidate_generated", "no_kb_match", "gateway_disabled",
  "provider_unavailable", "provider_error",
];

const NOTICE =
  "Shadow mode is active — guests never see the candidate answer. Review each row " +
  "and mark whether the generated candidate would have been safe to send.";

export default function ConciergeShadowPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async (nextOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (statusFilter) params.status = statusFilter;
      const d = await platformListConciergeShadow(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      const body = e?.response?.data;
      setError(
        e?.response?.status === 403
          ? "Administrator privileges required."
          : body?.message || "Could not reach the Platform API.",
      );
      setItems([]); setTotal(0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(0); }, []); // load once on mount

  const range = useMemo(() => {
    if (total === 0) return "0";
    return `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total.toLocaleString()}`;
  }, [offset, total]);

  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div data-testid="admin-concierge-shadow">
      <header className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="ed-overline mb-2 flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
            AI Gateway
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">
            Concierge — Shadow Review
          </h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">{NOTICE}</p>
        </div>
        <button
          type="button"
          onClick={() => load(0)}
          data-testid="cs-refresh"
          className="text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-4 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm flex items-center gap-2"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      <form
        onSubmit={(e) => { e.preventDefault(); load(0); }}
        className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5 items-end"
      >
        <div>
          <label className="ed-overline text-[0.55rem] mb-1.5 block">Status</label>
          <select
            data-testid="cs-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          >
            {STATUSES.map((s) => (
              <option key={s || "any"} value={s}>{s || "Any"}</option>
            ))}
          </select>
        </div>
        <div>
          <button
            data-testid="cs-apply"
            type="submit"
            className="w-full md:w-auto text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-4 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] rounded-sm"
          >
            Apply
          </button>
        </div>
      </form>

      <div className="border border-[var(--ed-rule)] rounded-sm bg-white">
        <div className="px-4 py-3 border-b border-[var(--ed-rule)] flex items-center justify-between">
          <p className="text-xs text-[var(--ed-ink-muted)]" data-testid="cs-range">
            {loading ? "Loading…" : `Showing ${range}`}
          </p>
          <div className="flex gap-2">
            <button
              type="button" data-testid="cs-prev"
              onClick={() => canPrev && load(Math.max(0, offset - PAGE_SIZE))}
              disabled={!canPrev || loading}
              className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm"
            >
              Prev
            </button>
            <button
              type="button" data-testid="cs-next"
              onClick={() => canNext && load(offset + PAGE_SIZE)}
              disabled={!canNext || loading}
              className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm"
            >
              Next
            </button>
          </div>
        </div>

        {error && (
          <div role="alert" data-testid="cs-error" className="p-6 flex items-start gap-3 text-sm">
            <AlertTriangle size={16} className="text-red-600 mt-0.5" />
            <p className="text-[var(--ed-ink)]">{error}</p>
          </div>
        )}

        {!error && !loading && items.length === 0 && (
          <p data-testid="cs-empty" className="p-8 text-center text-sm text-[var(--ed-ink-muted)]">
            No shadow interactions yet. When guests use the widget, questions
            and generated candidates will appear here for review.
          </p>
        )}

        {!error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cs-table">
              <thead className="bg-[var(--ed-paper)]">
                <tr className="text-left text-[var(--ed-ink-muted)]">
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">When (UTC)</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Question</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Status</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">KB</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Review</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-[var(--ed-rule)] hover:bg-[var(--ed-paper)]/50">
                    <td className="px-4 py-2.5 whitespace-nowrap font-mono text-[0.75rem] text-[var(--ed-ink-muted)]">
                      {(it.occurredAt || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-4 py-2.5 max-w-md text-[var(--ed-ink)] truncate">
                      {it.question}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={it.status} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[0.7rem] text-[var(--ed-ink-muted)]">
                      {(it.kbSources || []).length}
                    </td>
                    <td className="px-4 py-2.5">
                      <ReviewPill outcome={it.reviewOutcome} />
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        data-testid={`cs-open-${it.id}`}
                        onClick={() => setDetail(it.id)}
                        className="text-[0.7rem] uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm inline-flex items-center gap-1.5"
                      >
                        <MessageCircle size={12} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <DetailDrawer
          id={detail}
          onClose={() => setDetail(null)}
          onReviewed={() => { setDetail(null); load(offset); }}
        />
      )}
    </div>
  );
}

function DetailDrawer({ id, onClose, onReviewed }) {
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await platformGetConciergeShadow(id);
        setRow(d); setNotes(d?.reviewNotes || "");
      } catch (e) { setError(e?.response?.data?.message || "Load failed"); }
    })();
  }, [id]);

  const review = async (outcome) => {
    setBusy(true); setError(null);
    try {
      await platformReviewConciergeShadow(id, { outcome, notes: notes || null });
      onReviewed();
    } catch (e) {
      setError(e?.response?.data?.message || "Review failed");
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" data-testid="cs-detail"
         className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto border-l border-[var(--ed-rule)]">
        <div className="px-5 py-4 border-b border-[var(--ed-rule)] flex justify-between items-center">
          <h2 className="font-serif text-xl">Shadow interaction</h2>
          <button onClick={onClose} data-testid="cs-detail-close" aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        {error && (
          <div role="alert" className="m-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">
            {error}
          </div>
        )}
        {!row && !error && <p className="p-6 text-sm text-[var(--ed-ink-muted)]">Loading…</p>}
        {row && (
          <div className="p-5 space-y-5 text-sm">
            <MetaRow label="ID" value={row.id} mono />
            <MetaRow label="Occurred" value={(row.occurredAt || "").replace("T", " ").slice(0, 19)} mono />
            <MetaRow label="Conversation" value={row.conversationId} mono />
            <MetaRow label="Status" value={<StatusPill status={row.status} />} />
            <MetaRow label="Provider" value={row.candidateProvider || "—"} mono />
            <MetaRow label="Latency" value={`${row.candidateLatencyMs ?? 0} ms`} mono />

            <div>
              <p className="ed-overline text-[0.55rem] mb-1.5">Guest question</p>
              <p data-testid="cs-question" className="bg-[var(--ed-paper)]/60 border border-[var(--ed-rule)] p-3 rounded-sm whitespace-pre-wrap">
                {row.question}
              </p>
            </div>

            <div>
              <p className="ed-overline text-[0.55rem] mb-1.5">Candidate answer (server-side)</p>
              {row.candidateAnswer ? (
                <p data-testid="cs-candidate" className="bg-[var(--ed-emerald-tint)] border border-[var(--ed-emerald)] p-3 rounded-sm whitespace-pre-wrap">
                  {row.candidateAnswer}
                </p>
              ) : (
                <p className="text-[var(--ed-ink-muted)] italic">
                  No candidate was generated. {reasonFor(row.status)}
                </p>
              )}
            </div>

            <div>
              <p className="ed-overline text-[0.55rem] mb-1.5">Knowledge sources used</p>
              {(row.kbSources || []).length === 0 ? (
                <p className="text-[var(--ed-ink-muted)] italic">None</p>
              ) : (
                <ul className="divide-y divide-[var(--ed-rule)] border border-[var(--ed-rule)] rounded-sm bg-white">
                  {row.kbSources.map((s, i) => (
                    <li key={i} className="p-2.5">
                      <p className="text-[0.7rem] font-mono">{s.category}</p>
                      <p className="text-[0.65rem] font-mono text-[var(--ed-ink-muted)]">
                        {s.scopeType} · {s.itemId}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="ed-overline text-[0.55rem] mb-1.5">Reviewer notes</p>
              <textarea
                data-testid="cs-review-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={1000}
                className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button" data-testid="cs-review-approve"
                disabled={busy} onClick={() => review("approved")}
                className="text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 bg-[var(--ed-emerald)] text-white rounded-sm disabled:opacity-40"
              >
                Approve
              </button>
              <button
                type="button" data-testid="cs-review-edit"
                disabled={busy} onClick={() => review("needs_edit")}
                className="text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 bg-[var(--ed-ink)] text-white rounded-sm disabled:opacity-40"
              >
                Needs edit
              </button>
              <button
                type="button" data-testid="cs-review-reject"
                disabled={busy} onClick={() => review("rejected")}
                className="text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 border border-red-500 text-red-700 rounded-sm disabled:opacity-40"
              >
                Reject
              </button>
              {row.reviewOutcome && (
                <ReviewPill outcome={row.reviewOutcome} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function reasonFor(status) {
  switch (status) {
    case "no_kb_match":
      return "No relevant published KB content was found for this question.";
    case "gateway_disabled":
      return "The AI Gateway kill switch is OFF for this tenant.";
    case "provider_unavailable":
    case "provider_error":
      return "The provider was unavailable or returned an error.";
    default:
      return "";
  }
}

function MetaRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <p className="ed-overline text-[0.55rem] w-24 text-[var(--ed-ink-muted)]">{label}</p>
      <div className={`text-sm ${mono ? "font-mono text-[0.78rem]" : ""}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    candidate_generated: "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]",
    no_kb_match:         "bg-amber-50 text-amber-800 border-amber-300",
    gateway_disabled:    "bg-neutral-100 text-neutral-700 border-neutral-300",
    provider_unavailable:"bg-red-50 text-red-700 border-red-300",
    provider_error:      "bg-red-50 text-red-700 border-red-300",
  };
  return (
    <span className={`inline-block text-[0.6rem] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${map[status] || ""}`}>
      {status || "—"}
    </span>
  );
}

function ReviewPill({ outcome }) {
  if (!outcome) return <span className="text-[0.65rem] text-[var(--ed-ink-muted)] italic">Pending</span>;
  const map = {
    approved:   "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]",
    needs_edit: "bg-amber-50 text-amber-800 border-amber-300",
    rejected:   "bg-red-50 text-red-700 border-red-300",
  };
  return (
    <span className={`inline-block text-[0.6rem] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${map[outcome] || ""}`}>
      {outcome}
    </span>
  );
}
